import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { InstallHelpHost, UpdatePrompt } from "@/components/pwa/InstallApp";
import { AuthProvider } from "@/context/AuthContext";
import { DevAuthProvider } from "@/context/DevAuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { AuthCallback } from "@/components/admin/AuthCallback";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { AdminShell } from "@/components/admin/AdminShell";
import { DevShell } from "@/components/dev/DevShell";
import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import AppDetail from "@/pages/AppDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAppPage from "@/pages/AdminAppPage";
import AdminVip from "@/pages/AdminVip";
import AdminMessages from "@/pages/AdminMessages";
import AdminCapacity from "@/pages/AdminCapacity";
import AdminPurge from "@/pages/AdminPurge";
import AdminPromo from "@/pages/AdminPromo";
import AdminHealth from "@/pages/AdminHealth";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminAtelier from "@/pages/AdminAtelier";
import DevMessages from "@/pages/DevMessages";
import NotFound from "@/pages/NotFound";
import ThirdParty from "@/pages/ThirdParty";
import Hosting from "@/pages/Hosting";
import Videos from "@/pages/Videos";
import InstallGuide from "@/pages/InstallGuide";
import SharePage from "@/pages/SharePage";
import DevAuth from "@/pages/DevAuth";
import DevPassword from "@/pages/DevPassword";
import DevDashboard from "@/pages/DevDashboard";
import ClientSpace from "@/pages/ClientSpace";
import DevPaymentReturn from "@/pages/DevPaymentReturn";
import { QueueGate } from "@/components/queue/QueueGate";
import { I18nProvider } from "@/i18n/I18nContext";
import { StatsBeacon } from "@/components/layout/StatsBeacon";

function AppRouter() {
  const location = useLocation();
  // Google OAuth return: process session_id synchronously before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <StatsBeacon />
    <Routes>
      <Route element={<SiteLayout />}>
        <Route element={<QueueGate />}>
          <Route path="/" element={<Home />} />
          <Route path="/apps" element={<Catalogue />} />
          <Route path="/apps/:id" element={<AppDetail />} />
          <Route path="/developpeurs" element={<ThirdParty />} />
          <Route path="/developpeurs/apps/:id" element={<AppDetail market />} />
          <Route path="/developpeurs/:slug" element={<ThirdParty />} />
        </Route>
        <Route path="/creators" element={<Navigate to="/developpeurs" replace />} />
        <Route path="/hebergement" element={<Hosting />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/installer-un-apk" element={<InstallGuide />} />
        <Route path="/partage" element={<SharePage />} />
        <Route path="/espace-developpeur/connexion" element={<DevAuth />} />
        <Route path="/espace-client" element={<ClientSpace />} />
        <Route path="/espace-developpeur/mot-de-passe-oublie" element={<DevPassword />} />
        <Route path="/espace-developpeur/reinitialiser" element={<DevPassword />} />
        <Route element={<DevShell />}>
          <Route path="/espace-developpeur" element={<DevDashboard />} />
          <Route path="/espace-developpeur/apps/:id" element={<AdminAppPage />} />
          <Route path="/espace-developpeur/paiement" element={<DevPaymentReturn />} />
          <Route path="/espace-developpeur/messages" element={<DevMessages />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AdminShell />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/apps/:id" element={<AdminAppPage />} />
            <Route path="/admin/vip" element={<AdminVip />} />
            <Route path="/admin/developpeurs" element={<AdminDashboard section="developers" />} />
            <Route path="/admin/avis" element={<AdminDashboard section="reviews" />} />
            <Route path="/admin/statistiques" element={<AdminDashboard section="stats" />} />
            <Route path="/admin/verification-android" element={<AdminDashboard section="android" />} />
            <Route path="/admin/parametres" element={<AdminDashboard section="settings" />} />
            <Route path="/admin/messages" element={<AdminMessages />} />
            <Route path="/admin/capacite" element={<AdminCapacity />} />
            <Route path="/admin/purge" element={<AdminPurge />} />
            <Route path="/admin/kit-pub" element={<AdminPromo />} />
            <Route path="/admin/sante" element={<AdminHealth />} />
            <Route path="/admin/abonnements" element={<AdminSubscriptions />} />
            <Route path="/admin/atelier" element={<AdminAtelier />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <SiteProvider>
          <AuthProvider>
            <DevAuthProvider>
              <AppRouter />
              <Toaster theme="dark" position="top-center" richColors />
              <UpdatePrompt />
              <InstallHelpHost />
            </DevAuthProvider>
          </AuthProvider>
        </SiteProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
